import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { IonButton, IonIcon } from '@ionic/react';
import { refreshOutline, eyeOutline, eyeOffOutline } from 'ionicons/icons';
import AuthLayout from 'layout/auth/Auth.layout';
import { ForgottenpwdWrapper, ForgottenpwdTitle, ForgottenpwdInstruction } from 'pages/forgottenpwd/Forgottenpwd.styled';
import styled from 'styled-components';
import { prem } from 'styles/helpers/spacing';
import useFetch from 'hooks/api/useFetch';
import useAPIAuth from 'hooks/api/useAPIAuth';
import Textfield, { TextfieldProps } from 'components/common/form/textfield/Textfield';
import { FORM } from 'constants/form';
import { TextFieldTypes } from '@ionic/core';
import useFormHandler from 'hooks/useFormHandler';

const ResetPwdWrapper = styled(ForgottenpwdWrapper)`
  .password-field-wrapper {
    position: relative;
    margin-bottom: ${prem(20)};
    
    ion-input {
      input.native-input {
        padding-right: ${prem(45)} !important;
      }
    }
    
    .toggle-password {
      position: absolute;
      right: ${prem(10)};
      top: ${prem(24)};
      z-index: 10;
      --color: #666666;
      
      &:hover {
        --color: #333333;
      }
    }
  }

  .submit-button {
    margin-top: ${prem(30)};
    margin-bottom: ${prem(20)};
    
    &:disabled {
      opacity: 0.5;
    }
  }
`;

const ResetPwdError = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: ${prem(8)};
  padding: ${prem(20)};
  margin: ${prem(20)} 0;
  text-align: center;
  font-size: ${prem(16)};
`;

const ResetPwdMessage = styled.div`
  margin: ${prem(20)} 0;
  text-align: center;
  
  &.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  &.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  border-radius: ${prem(8)};
  padding: ${prem(20)};
  font-size: ${prem(16)};
`;

const ResetPwdReminder = styled.div`
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
  border-radius: ${prem(8)};
  padding: ${prem(15)};
  margin: ${prem(20)} 0;
  text-align: center;
  font-size: ${prem(14)};
  font-weight: 500;
`;

const PasswordStrengthIndicator = styled.div`
  margin-top: ${prem(10)};
  text-align: left;
  
  .strength-item {
    display: flex;
    align-items: center;
    margin-bottom: ${prem(5)};
    font-size: ${prem(14)};
    color: #dc3545;
    
    &::before {
      content: '✗';
      margin-right: ${prem(8)};
      font-weight: bold;
    }
    
    &.valid {
      color: #28a745;
      
      &::before {
        content: '✓';
      }
    }
  }
`;

const PasswordMatchIndicator = styled.div`
  margin-top: ${prem(10)};
  text-align: left;
  
  .match-item {
    display: flex;
    align-items: center;
    font-size: ${prem(14)};
    color: #dc3545;
    
    &::before {
      content: '✗';
      margin-right: ${prem(8)};
      font-weight: bold;
    }
    
    &.valid {
      color: #28a745;
      
      &::before {
        content: '✓';
      }
    }
  }
`;

const ResetPwd: React.FC = () => {
  const { t } = useTranslation('translation');
  const location = useLocation();

  const { showLoading, hideLoading } = useFetch<any>();
  const { validateResetToken, resetPasswordWithToken } = useAPIAuth();

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasLetters: false,
    hasNumbers: false,
    hasMinLength: false,
    isValid: false
  });
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [tokenId, setTokenId] = useState<string>('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [resetMessage, setResetMessage] = useState('');

  const { fields, getFieldByName, updateFieldValue } = useFormHandler([
    {
      name: 'password',
      value: '',
      required: true,
      type: FORM.TYPES.PASSWORD as TextFieldTypes,
    },
    {
      name: 'confirmPassword',
      value: '',
      required: true,
      type: FORM.TYPES.PASSWORD as TextFieldTypes,
    },
  ]);

  // Fonction pour vérifier la force du mot de passe
  const checkPasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength({ hasLetters: false, hasNumbers: false, hasMinLength: false, isValid: false });
      return false;
    }

    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasMinLength = password.length >= 6;
    const isValid = hasMinLength && hasLetters && hasNumbers;

    setPasswordStrength({ hasLetters, hasNumbers, hasMinLength, isValid });
    return isValid;
  };

  // Fonction pour générer un mot de passe aléatoire
  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let newPassword = '';

    // S'assurer qu'il y a au moins une lettre et un chiffre
    newPassword += 'A'; // Une majuscule
    newPassword += 'a'; // Une minuscule
    newPassword += '1'; // Un chiffre

    // Remplir le reste aléatoirement
    for (let i = 3; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    // Mélanger le mot de passe
    const shuffledPassword = newPassword.split('').sort(() => Math.random() - 0.5).join('');

    updateFieldValue('password', shuffledPassword);
    updateFieldValue('confirmPassword', shuffledPassword);
    checkPasswordStrength(shuffledPassword);
    checkPasswordsMatch(shuffledPassword, shuffledPassword);
  };

  // Fonction pour vérifier si les mots de passe correspondent
  const checkPasswordsMatch = (pwd: string, confirmPwd: string) => {
    const match = pwd !== '' && confirmPwd !== '' && pwd === confirmPwd;
    setPasswordsMatch(match);
    return match;
  };

  // Gérer le changement de mot de passe
  const handlePasswordChange = (value?: string) => {
    const pwd = value || '';
    updateFieldValue('password', pwd);
    checkPasswordStrength(pwd);
    checkPasswordsMatch(pwd, getFieldByName('confirmPassword').value as string);
  };

  // Gérer le changement de confirmation de mot de passe
  const handleConfirmPasswordChange = (value?: string) => {
    const confirmPwd = value || '';
    updateFieldValue('confirmPassword', confirmPwd);
    checkPasswordsMatch(getFieldByName('password').value as string, confirmPwd);
  };

  // Récupérer les paramètres de l'URL au montage
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token_id');
    const origin = searchParams.get('origin');

    console.log('Token ID from URL:', token);

    if (!token) {
      setTokenValid(false);
      setIsLoading(false);
      return;
    }

    setTokenId(token);

    // Stocker l'origin en localStorage si fourni
    if (origin) {
      localStorage.setItem('reset_pwd_origin', origin);
    }

    // Validation du token via l'API
    const validateToken = async () => {
      try {
        const isValid = await validateResetToken(token);
        setTokenValid(isValid || false);
      } catch (error) {
        console.error('Error validating token:', error);
        setTokenValid(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [location.search]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const password = getFieldByName('password').value as string;
    const confirmPassword = getFieldByName('confirmPassword').value as string;

    if (!password || !confirmPassword || !tokenId) return;

    // Les validations sont maintenant gérées en temps réel
    if (!passwordsMatch || !passwordStrength.isValid) {
      return;
    }

    try {
      showLoading();
      setResetStatus('idle');
      setResetMessage('');
      
      console.log('Resetting password with token:', tokenId);
      
      // Appel à l'API pour réinitialiser le mot de passe
      const resetSuccess = await resetPasswordWithToken(tokenId, password);
      
      if (!resetSuccess) {
        throw new Error('Password reset failed');
      }

      // Succès
      setResetStatus('success');
      setResetMessage(t('resetpwd.success'));
      
      // Rediriger après 2 secondes
      setTimeout(() => {
        const origin = localStorage.getItem('reset_pwd_origin');
        if (origin) {
          localStorage.removeItem('reset_pwd_origin');
          window.location.href = origin;
        } else {
          // Rediriger vers la page de connexion ou d'accueil
          window.location.href = '/login';
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error resetting password:', error);
      setResetStatus('error');
      setResetMessage(t('resetpwd.errors.general'));
    } finally {
      hideLoading();
    }
  };

  if (isLoading) {
    return (
      <AuthLayout>
        <ResetPwdWrapper>
          <ForgottenpwdTitle>{t('resetpwd.loading')}</ForgottenpwdTitle>
        </ResetPwdWrapper>
      </AuthLayout>
    );
  }

  if (!tokenValid) {
    return (
      <AuthLayout>
        <ResetPwdWrapper>
          <ForgottenpwdTitle>{t('resetpwd.title')}</ForgottenpwdTitle>
          <ResetPwdError>
            {t('resetpwd.errors.invalidToken')}
          </ResetPwdError>
        </ResetPwdWrapper>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <ResetPwdWrapper>
        <ForgottenpwdTitle>{t('resetpwd.title')}</ForgottenpwdTitle>
        <ForgottenpwdInstruction>
          {t('resetpwd.instruction')}
        </ForgottenpwdInstruction>

        <ResetPwdReminder>
          {t('resetpwd.reminder')}
        </ResetPwdReminder>

        {resetStatus !== 'idle' && resetMessage && (
          <ResetPwdMessage className={resetStatus}>
            {resetMessage}
          </ResetPwdMessage>
        )}

        <form noValidate onSubmit={onSubmit} autoComplete="off">
          <IonButton
            fill="clear"
            size="small"
            onClick={generateRandomPassword}
            style={{ marginBottom: '20px' }}
          >
            <IonIcon icon={refreshOutline} slot="start" />
            {t('resetpwd.generatePassword')}
          </IonButton>

          <div className="password-field-wrapper">
            <Textfield
              {...({
                ...getFieldByName('password', fields),
                type: showPassword ? 'text' : 'password',
                label: t('resetpwd.form.newPassword') || '',
                onChange: handlePasswordChange,
              } as TextfieldProps)}
            />
            <IonButton
              fill="clear"
              size="small"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              <IonIcon icon={showPassword ? eyeOffOutline : eyeOutline} />
            </IonButton>
          </div>

          <PasswordStrengthIndicator>
            <div className={`strength-item ${passwordStrength.hasMinLength ? 'valid' : ''}`}>
              {t('resetpwd.validation.minLength')}
            </div>
            <div className={`strength-item ${passwordStrength.hasLetters ? 'valid' : ''}`}>
              {t('resetpwd.validation.hasLetters')}
            </div>
            <div className={`strength-item ${passwordStrength.hasNumbers ? 'valid' : ''}`}>
              {t('resetpwd.validation.hasNumbers')}
            </div>
          </PasswordStrengthIndicator>

          <div className="password-field-wrapper">
            <Textfield
              {...({
                ...getFieldByName('confirmPassword', fields),
                type: showConfirmPassword ? 'text' : 'password',
                label: t('resetpwd.form.confirmPassword') || '',
                onChange: handleConfirmPasswordChange,
              } as TextfieldProps)}
            />
            <IonButton
              fill="clear"
              size="small"
              className="toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              <IonIcon icon={showConfirmPassword ? eyeOffOutline : eyeOutline} />
            </IonButton>
          </div>

          {getFieldByName('confirmPassword').value && (
            <PasswordMatchIndicator>
              <div className={`match-item ${passwordsMatch ? 'valid' : ''}`}>
                {t('resetpwd.validation.passwordsMatch')}
              </div>
            </PasswordMatchIndicator>
          )}

          <IonButton
            expand="block"
            type="submit"
            disabled={!getFieldByName('password').value || !getFieldByName('confirmPassword').value || !passwordStrength.isValid || !passwordsMatch || resetStatus === 'success'}
            className="submit-button"
          >
            {resetStatus === 'success' ? t('resetpwd.redirecting') : t('resetpwd.form.submit')}
          </IonButton>
        </form>
      </ResetPwdWrapper>
    </AuthLayout>
  );
};

export default ResetPwd;